/* eslint-disable no-prototype-builtins */
const roadColor = '#b0aeae';
const labelColor = '#000000';

/**
 * Return all Google map style options for a custom look
 *
 * Google has a styling wizard https://mapstyle.withgoogle.com/ that can export these JSON-styles.
 *
 * @return {object} map styles
 */
function getMapStyles() {
  return {
    zoom: 12,
    maxZoom: 12,
    disableDefaultUI: true,
    mapTypeId: window.google.maps.MapTypeId.ROADMAP,
    scrollwheel: false,
    draggable: !('ontouchend' in document),
    styles: [
      {
        featureType: 'administrative',
        elementType: 'labels.text.fill',
        stylers: [
          {
            color: labelColor,
          },
        ],
      },
      {
        featureType: 'landscape',
        elementType: 'all',
        stylers: [
          {
            color: '#ffffff',
          },
        ],
      },
      {
        featureType: 'poi',
        elementType: 'all',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'road',
        elementType: 'all',
        stylers: [
          {
            saturation: -100,
          },
          {
            lightness: 45,
          },
        ],
      },
      {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [
          {
            color: roadColor,
          },
        ],
      },
      {
        featureType: 'road',
        elementType: 'labels',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'road',
        elementType: 'labels.text',
        stylers: [
          {
            visibility: 'on',
          },
        ],
      },
      {
        featureType: 'road.highway',
        elementType: 'all',
        stylers: [
          {
            visibility: 'simplified',
          },
          {
            color: roadColor,
          },
        ],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [
          {
            color: roadColor,
          },
        ],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry.fill',
        stylers: [
          {
            visibility: 'on',
          },
        ],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry.stroke',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'road.highway',
        elementType: 'labels',
        stylers: [
          {
            visibility: 'off',
          },
          {
            color: labelColor,
          },
        ],
      },
      {
        featureType: 'road.highway.controlled_access',
        elementType: 'geometry',
        stylers: [
          {
            color: roadColor,
          },
        ],
      },
      {
        featureType: 'road.highway.controlled_access',
        elementType: 'labels',
        stylers: [
          {
            visibility: 'off',
          },
          {
            color: labelColor,
          },
        ],
      },
      {
        featureType: 'road.highway.controlled_access',
        elementType: 'labels.text',
        stylers: [
          {
            color: '#ffffff',
          },
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'road.highway.controlled_access',
        elementType: 'labels.text.fill',
        stylers: [
          {
            color: labelColor,
          },
        ],
      },
      {
        featureType: 'road.highway.controlled_access',
        elementType: 'labels.text.stroke',
        stylers: [
          {
            color: labelColor,
          },
        ],
      },
      {
        featureType: 'road.arterial',
        elementType: 'geometry',
        stylers: [
          {
            color: roadColor,
          },
        ],
      },
      {
        featureType: 'road.arterial',
        elementType: 'labels',
        stylers: [
          {
            visibility: 'on',
          },
        ],
      },
      {
        featureType: 'road.arterial',
        elementType: 'labels.text',
        stylers: [
          {
            visibility: 'on',
          },
        ],
      },
      {
        featureType: 'road.arterial',
        elementType: 'labels.icon',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'road.local',
        elementType: 'labels',
        stylers: [
          {
            visibility: 'on',
          },
        ],
      },
      {
        featureType: 'transit',
        elementType: 'all',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'transit',
        elementType: 'geometry',
        stylers: [
          {
            color: '#00ff19',
          },
        ],
      },
      {
        featureType: 'transit.line',
        elementType: 'geometry',
        stylers: [
          {
            color: roadColor,
          },
        ],
      },
      {
        featureType: 'water',
        elementType: 'all',
        stylers: [
          {
            color: '#d1d1d1',
          },
          {
            visibility: 'on',
          },
        ],
      },
    ],
  };
}

/**
 * Convert Google map styles to GET-parameters for a Google Static Map API
 * @param {object} mapStyles google map styles
 * @returns {string} GET-parameter string
 */
function mapStylesToGetParameters(mapStyles) {
  const result = [];

  mapStyles.styles.forEach(function(v, i, a) {
    let style = '';

    if (v.stylers.length > 0) {
      // Needs to have a style rule to be valid.
      style += (v.hasOwnProperty('featureType') ? 'feature:' + v.featureType : 'feature:all') + '|';
      style += (v.hasOwnProperty('elementType') ? 'element:' + v.elementType : 'element:all') + '|';
      v.stylers.forEach(function(val, i, a) {
        const propertyname = Object.keys(val)[0];
        const propertyval = val[propertyname].toString().replace('#', '0x');
        style += propertyname + ':' + propertyval + '|';
      });
    }

    result.push('style=' + encodeURIComponent(style));
  });

  return result.join('&');
}

function calculateDistance(loc1, loc2) {
  loc1.lat = parseFloat(loc1.lat);
  loc1.lon = parseFloat(loc1.lon);
  loc2.lat = parseFloat(loc2.lat);
  loc2.lon = parseFloat(loc2.lon);

  var rad = function(x) {
    return (x * Math.PI) / 180;
  };

  var R = 6371; // earth's mean radius in km
  var dLat = rad(loc2.lat - loc1.lat);
  var dLong = rad(loc2.lon - loc1.lon);

  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(rad(loc1.lat)) * Math.cos(rad(loc2.lat)) * Math.sin(dLong / 2) * Math.sin(dLong / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  var d = R * c;

  return d.toFixed(3);
}

function userLatlng(timeout, cb) {
  //timeout in seconds

  var latlng = {};

  if (navigator.geolocation) {
    var seconds = timeout || 10;
    var timeoutVal = 1000 * seconds;

    navigator.geolocation.getCurrentPosition(
      function(position) {
        latlng.lat = position.coords.latitude;
        latlng.lon = position.coords.longitude;
        latlng.gps = true;

        if (cb) cb(latlng);
      },
      function(error) {
        latlng.lat = typeof geoip_latitude === 'function' ? window.geoip_latitude() : null;
        latlng.lon = typeof geoip_longitude === 'function' ? window.geoip_longitude() : null;
        latlng.gps = false;

        if (cb) cb(latlng);
      },
      {
        timeout: timeoutVal,
        maximumAge: 0,
      },
    );
  } else {
    latlng.lat = typeof geoip_latitude === 'function' ? window.geoip_latitude() : null;
    latlng.lon = typeof geoip_longitude === 'function' ? window.geoip_longitude() : null;
    latlng.gps = false;

    if (cb) cb(latlng);
  }
}

function userGeolocation(success, error) {
  if (navigator.geolocation) {
    var timeout = 10;
    var timeoutms = 1000 * timeout;

    navigator.geolocation.getCurrentPosition(success, error, {
      timeout: timeoutms,
      maximumAge: 0,
    });
  } else {
    if (error) error();
  }
}

module.exports = {
  getMapStyles,
  mapStylesToGetParameters,
  calculateDistance,
  userGeolocation,
  userLatlng,
  APIKEY: 'AIzaSyCZ0fg8-5eqUXat4FkPgG-jN4I_d-8tRmk',
  markerIcon: 'https://laakkonen.cdn.crasman.fi/pub/web/v2/img/map-pin-v3.png',
};
